import { useEffect, useState } from "react";
import BossDialog from "../../../components/BossDialog";
import * as API from "../../../framework/API/api";
import { ENTITYNAME, LOGEVENT, MODULE, TOAST_TYPES, TRANSMISSION_ACTION } from "../../../framework/constant/constant";
import { EditPageHeader } from "../../../components/EditPageHeader";
import { getSelectedState, Grid, GridColumn } from '@progress/kendo-react-grid';
import { getter } from '@progress/kendo-data-query';
import { TimeRangePicker } from '../../../framework/forms/helpercomponents/timepicker/TimeRangePicker';
import ToastNotification from '../../../components/ToastNotification';
import { useTranslation } from '../../../locale/useTranslation';
import moment from "moment";
import { DateTimeCell } from "../../../framework/forms/helpercomponents/CustomGridCells/DateCell";
import { ArrayCell } from "../../../framework/forms/helpercomponents/CustomGridCells/ArrayCell";
import { Loader } from "../../../framework/forms/helpercomponents/Loader";

const SELECTED_FIELD = "selected";
const DATA_ITEM_KEY = "_id";
const idGetter = getter(DATA_ITEM_KEY);

const EditSeriesTransmissionForm = (props) => {

    const { onClose, item = {} } = props;

    const lang = useTranslation();

    const getInitialFromEpisode = () => {
        const epNum = item?.mediaEpisode?.EpisodeNumber ?? item?.MediaEpisode?.EpisodeNumber ?? item?.EpisodeNumber;
        return epNum !== undefined && epNum !== null && epNum !== "" ? Number(epNum) : "";
    };

    const [isLoading, setIsLoading] = useState(false);
    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const [gridData, setGridData] = useState([]);
    const [selectedState, setSelectedState] = useState({});
    const [bulkStartDate, setBulkStartDate] = useState("");
    const [bulkStartTime, setBulkStartTime] = useState("00:00");
    const [bulkEndDate, setBulkEndDate] = useState("");
    const [bulkEndTime, setBulkEndTime] = useState("00:00");
    const [updateStartDate, setUpdateStartDate] = useState(false);
    const [updateEndDate, setUpdateEndDate] = useState(false);
    const [useStartStack, setUseStartStack] = useState(false);
    const [startStackOffset, setStartStackOffset] = useState(0);
    const [useEndStack, setUseEndStack] = useState(false);
    const [endStackOffset, setEndStackOffset] = useState(0);
    const [episodeRange, setEpisodeRange] = useState({
        from: getInitialFromEpisode(),
        to: ""
    });

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        const selectedTransmissions = gridData.filter(x => selectedState[idGetter(x)]);
        if (selectedTransmissions.length === 1) {
            const tx = selectedTransmissions[0];
            const startDate = tx.startDate;
            const endDate = tx.endDate;
            setBulkStartDate(startDate ? moment.utc(startDate).format('YYYY-MM-DD') : "");
            setBulkStartTime(startDate ? moment.utc(startDate).format('HH:mm') : "00:00");
            setBulkEndDate(endDate ? moment.utc(endDate).format('YYYY-MM-DD') : "");
            setBulkEndTime(endDate ? moment.utc(endDate).format('HH:mm') : "00:00");
            setUpdateStartDate(!!startDate);
            setUpdateEndDate(!!endDate);
        } else {
            setBulkStartDate("");
            setBulkStartTime("00:00");
            setBulkEndDate("");
            setBulkEndTime("00:00");
            setUpdateStartDate(false);
            setUpdateEndDate(false);
        }
    }, [selectedState, gridData]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const seasonId = item?.mediaEpisode?.Season_id ?? item?.MediaEpisode?.Season_id;

            let res = await API.getTransmissions({ query: ["season_id", "=", seasonId] });
            const data = res.data ?? [];

            // Sort by episode number (ascending)
            const sortedData = data.sort((a, b) => {
                const epA = Number(a?.mediaEpisode?.EpisodeNumber ?? a?.MediaEpisode?.EpisodeNumber ?? a?.EpisodeNumber ?? 0);
                const epB = Number(b?.mediaEpisode?.EpisodeNumber ?? b?.MediaEpisode?.EpisodeNumber ?? b?.EpisodeNumber ?? 0);
                return epA - epB;
            });

            setGridData(sortedData);

            if (sortedData.length > 0) {
                const epNums = sortedData.map(x => parseInt(x?.mediaEpisode?.EpisodeNumber ?? x?.MediaEpisode?.EpisodeNumber ?? x?.EpisodeNumber)).filter(x => !isNaN(x));
                if (epNums.length > 0) {
                    const maxEp = Math.max(...epNums);
                    const minEp = Math.min(...epNums);
                    const initialFrom = getInitialFromEpisode();
                    setEpisodeRange({
                        from: initialFrom !== "" ? initialFrom : minEp,
                        to: maxEp
                    });
                }
            }
        } catch (error) {
            console.error("Error loading transmission data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

    const onSelectionChange = (event) => {
        const syntheticEvent = event.syntheticEvent;
        const isShiftKey = syntheticEvent && syntheticEvent.shiftKey;
        const clickedItem = event.dataItem;
        const clickedIndex = filteredGridData.findIndex(x => idGetter(x) === idGetter(clickedItem));

        if (isShiftKey && lastSelectedIndex !== null && clickedIndex !== -1) {
            const start = Math.min(lastSelectedIndex, clickedIndex);
            const end = Math.max(lastSelectedIndex, clickedIndex);
            const rangeItems = filteredGridData.slice(start, end + 1);

            const targetSelected = !selectedState[idGetter(clickedItem)];

            const newSelectedState = { ...selectedState };
            rangeItems.forEach((item) => {
                if (targetSelected) {
                    newSelectedState[idGetter(item)] = true;
                } else {
                    delete newSelectedState[idGetter(item)];
                }
            });
            setSelectedState(newSelectedState);
        } else {
            const newSelectedState = getSelectedState({
                event,
                selectedState: selectedState,
                dataItemKey: DATA_ITEM_KEY,
            });
            setSelectedState(newSelectedState);
        }

        if (clickedIndex !== -1) {
            setLastSelectedIndex(clickedIndex);
        }
    };

    const onHeaderSelectionChange = (event) => {
        const checkboxElement = event.syntheticEvent.target;
        const checked = checkboxElement.checked;
        const newSelectedState = { ...selectedState };
        event.dataItems.forEach((item) => {
            if (checked) {
                newSelectedState[idGetter(item)] = true;
            } else {
                delete newSelectedState[idGetter(item)];
            }
        });
        setSelectedState(newSelectedState);
    };

    const handleSelectRange = () => {
        const from = episodeRange.from !== "" ? parseInt(episodeRange.from) : null;
        const to = episodeRange.to !== "" ? parseInt(episodeRange.to) : null;

        if (from === null && to === null) {
            const newSelectedState = { ...selectedState };
            filteredGridData.forEach((item) => {
                newSelectedState[idGetter(item)] = true;
            });
            setSelectedState(newSelectedState);
            ToastNotification({
                type: TOAST_TYPES.SUCCESS,
                title: lang.success_message_title,
                message: `Selected ${filteredGridData.length} item(s).`
            });
            return;
        }

        const itemsToSelect = gridData.filter((item) => {
            const epNum = parseInt(item?.mediaEpisode?.EpisodeNumber);
            if (isNaN(epNum)) return false;
            if (from !== null && epNum < from) return false;
            if (to !== null && epNum > to) return false;
            return true;
        });

        if (itemsToSelect.length === 0) {
            ToastNotification({
                type: TOAST_TYPES.WARNING,
                title: lang.warning_message_title,
                message: "No episodes found within the specified range."
            });
            return;
        }

        const newSelectedState = { ...selectedState };
        itemsToSelect.forEach((item) => {
            newSelectedState[idGetter(item)] = true;
        });

        setSelectedState(newSelectedState);
        ToastNotification({
            type: TOAST_TYPES.SUCCESS,
            title: lang.success_message_title,
            message: `Selected ${itemsToSelect.length} episode(s) in range.`
        });
    };

    const handleDeselectRange = () => {
        const from = episodeRange.from !== "" ? parseInt(episodeRange.from) : null;
        const to = episodeRange.to !== "" ? parseInt(episodeRange.to) : null;

        const itemsToDeselect = gridData.filter((item) => {
            const epNum = parseInt(item?.mediaEpisode?.EpisodeNumber);
            if (isNaN(epNum)) return false;
            if (from !== null && epNum < from) return false;
            if (to !== null && epNum > to) return false;
            return true;
        });

        const newSelectedState = { ...selectedState };
        itemsToDeselect.forEach((item) => {
            delete newSelectedState[idGetter(item)];
        });

        setSelectedState(newSelectedState);
    };

    const handleBulkUpdateDates = () => {
        if (!updateStartDate && !updateEndDate) {
            ToastNotification({
                type: TOAST_TYPES.WARNING,
                title: lang.warning_message_title,
                message: lang.please_select_at_least_one_field_to_update_error_message
            });
            return;
        }

        if (updateStartDate && (!bulkStartDate || !bulkStartTime || bulkStartTime.includes('_'))) {
            ToastNotification({
                type: TOAST_TYPES.WARNING,
                title: lang.warning_message_title,
                message: lang.please_select_start_date_error_message
            });
            return;
        }

        if (updateEndDate && (!bulkEndDate || !bulkEndTime || bulkEndTime.includes('_'))) {
            ToastNotification({
                type: TOAST_TYPES.WARNING,
                title: lang.warning_message_title,
                message: lang.please_select_end_date_error_message
            });
            return;
        }

        const selectedTransmissions = gridData.filter(x => selectedState[idGetter(x)]);
        if (selectedTransmissions.length === 0) {
            ToastNotification({
                type: TOAST_TYPES.WARNING,
                title: lang.warning_message_title,
                message: lang.no_episode_selected_error_message
            });
            return;
        }

        let hasDateError = false;
        const updatedGridData = gridData.map(item => {
            if (selectedState[idGetter(item)]) {
                const selectedIndex = selectedTransmissions.findIndex(x => idGetter(x) === idGetter(item));

                let newStartDate = item.startDate;
                let newEndDate = item.endDate;

                if (updateStartDate) {
                    const startOffsetDays = useStartStack ? selectedIndex * startStackOffset : 0;
                    newStartDate = moment.utc(`${bulkStartDate}T${bulkStartTime}`).add(startOffsetDays, 'days').valueOf();
                }
                if (updateEndDate) {
                    const endOffsetDays = useEndStack ? selectedIndex * endStackOffset : 0;
                    newEndDate = moment.utc(`${bulkEndDate}T${bulkEndTime}`).add(endOffsetDays, 'days').valueOf();
                }

                if (newStartDate && newEndDate && newStartDate > newEndDate) {
                    hasDateError = true;
                }

                return {
                    ...item,
                    startDate: newStartDate,
                    endDate: newEndDate
                };
            }
            return item;
        });

        if (hasDateError) {
            ToastNotification({
                type: TOAST_TYPES.WARNING,
                title: lang.warning_message_title,
                message: lang.start_date_cannot_be_greater_than_end_date_error_message
            });
            return;
        }

        setGridData(updatedGridData);
        setUseStartStack(false);
        setStartStackOffset(0);
        setUseEndStack(false);
        setEndStackOffset(0);

        ToastNotification({
            type: TOAST_TYPES.SUCCESS,
            title: lang.success_message_title,
            message: `Updated dates for ${selectedTransmissions.length} selected item(s).`
        });
    };

    const isValid = () => {
        const selectedTransmissions = gridData.filter(x => selectedState[idGetter(x)]);
        if (selectedTransmissions.length === 0) {
            ToastNotification({
                type: TOAST_TYPES.ERROR,
                title: lang.invalid_error_message_title,
                message: lang.please_select_atleast_one_item
            });
            return false;
        }

        for (const transmission of selectedTransmissions) {
            const startDate = transmission.startDate;
            const endDate = transmission.endDate;
            if (startDate && endDate && startDate > endDate) {
                ToastNotification({
                    type: TOAST_TYPES.ERROR,
                    title: lang.invalid_error_message_title,
                    message: `For Episode ${transmission.mediaEpisode?.EpisodeNumber || ""}: Start Date cannot be greater than End Date.`
                });
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!isValid()) return;
        setIsSaveLoading(true);
        try {
            const selectedTransmissions = gridData.filter(x => selectedState[idGetter(x)]);
            const payload = selectedTransmissions.map((x) => {
                return {
                    _id: x._id,
                    startDate: x.startDate,
                    endDate: x.endDate,
                    action: TRANSMISSION_ACTION.Update
                };
            });

            let res = await API.saveData(ENTITYNAME.Transmissions, payload);
            if (res.success) {
                let responseData = Array.isArray(res.data) && res.data?.length > 0 ? res.data.map(x => x.data) : [];
                for (const item of responseData) {
                    let logData = { event: LOGEVENT.UPDATE_TRANSMISSION, module: MODULE.TRANSMISSION_BROWSER, data: item, message: "Series Transmission updated successfully" };
                    await API.SaveLogs(logData);
                }
                ToastNotification({ type: TOAST_TYPES.SUCCESS, title: lang.success_message_title, message: res.message });
                onClose();
            } else {
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: res.message });
            }
        } catch (error) {
            console.error("Error submitting series transmission dates:", error);
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: error.message });
        } finally {
            setIsSaveLoading(false);
        }
    };

    const epNums = gridData.map(x => parseInt(x.mediaEpisode?.EpisodeNumber)).filter(x => !isNaN(x));
    const minEp = epNums.length > 0 ? Math.min(...epNums) : null;
    const maxEp = epNums.length > 0 ? Math.max(...epNums) : null;
    const baseTitle = item?.mediaEpisode?.Title ?? item?.MediaEpisode?.Title ?? item?.Title ?? item?.WorkingTitle ?? "";
    const formTitle = `${baseTitle}${minEp !== null && maxEp !== null ? ` (Episodes: ${minEp} to ${maxEp})` : ""}`;

    const filteredGridData = gridData.filter((item) => {
        const epNum = parseInt(item?.mediaEpisode?.EpisodeNumber);
        if (isNaN(epNum)) return true;
        const from = episodeRange.from !== "" ? parseInt(episodeRange.from) : null;
        const to = episodeRange.to !== "" ? parseInt(episodeRange.to) : null;
        if (from !== null && epNum < from) return false;
        if (to !== null && epNum > to) return false;
        return true;
    });

    const gridDataWithSelection = filteredGridData.map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
    }));

    const selectedTransmissions = gridData.filter(x => selectedState[idGetter(x)]);

    return (
        <BossDialog
            width="80%"
            title={"Updatee Series Transmission" + (gridData.length > 0 ? ` (Total Transmissions: ${gridData.length})` : "")}
            onClose={onClose}
        >
            <style>{`
                @keyframes slideInRight {
                    from {
                        transform: translateX(30px);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
            `}</style>
            <div className="d-flex justify-content-between align-items-center">
                <EditPageHeader saveButtonTitle={"Save Changes"} showTitle={false} onSubmit={handleSubmit} onCancel={onClose} isLoading={isSaveLoading} />
                {formTitle && (
                    <div style={{
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        padding: "6px 12px",
                        display: "flex",
                        alignItems: "center",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                        whiteSpace: "nowrap",
                        flexShrink: 0
                    }}>
                        <i className="fa fa-film" style={{ color: "#007bff", fontSize: "15px", marginRight: "8px" }} />
                        <span style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
                            {formTitle}
                        </span>
                    </div>
                )}
            </div>
            <div style={{ position: 'relative', marginTop: '15px' }}>
                {/* Left side: Grid showing transmission details */}
                <div
                    style={{
                        width: selectedTransmissions.length > 0 ? "calc(100% - 430px)" : "100%",
                        transition: 'width 0.2s ease',
                        paddingRight: selectedTransmissions.length > 0 ? "10px" : "0px"
                    }}
                >
                    <div style={{ marginBottom: "15px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <h6 style={{ fontWeight: "600", color: "#333", borderBottom: "2px solid #007bff", paddingBottom: "5px", display: "inline-block", margin: 0 }}>
                                {lang.transmissions_header || "Transmissions"}
                            </h6>
                            {selectedTransmissions.length === 0 && (
                                <span style={{ marginLeft: "15px", fontSize: "13px", color: "#6c757d", backgroundColor: "#f8f9fa", padding: "4px 10px", borderRadius: "4px", border: "1px solid #dee2e6" }}>
                                    <i className="fa fa-info-circle me-1" style={{ color: "#007bff" }}></i>
                                    {lang.please_select_item_to_show_bulk_update_panel_error_message}
                                </span>
                            )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ fontSize: "13px", fontWeight: "600", color: "#495057" }}>
                                {lang.episode_range_label}:
                            </span>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                placeholder="From"
                                style={{ width: "70px", borderRadius: "4px" }}
                                value={episodeRange.from}
                                onChange={(e) => setEpisodeRange(prev => ({ ...prev, from: e.target.value === "" ? "" : Number(e.target.value) }))}
                            />
                            <span style={{ fontSize: "13px", color: "#495057" }}>to</span>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                placeholder="To"
                                style={{ width: "70px", borderRadius: "4px" }}
                                value={episodeRange.to}
                                onChange={(e) => setEpisodeRange(prev => ({ ...prev, to: e.target.value === "" ? "" : Number(e.target.value) }))}
                            />
                            <button
                                type="button"
                                className="k-button k-button-sm k-button-solid k-button-solid-primary"
                                onClick={handleSelectRange}
                                style={{ borderRadius: "4px", height: "30px", fontWeight: "600" }}
                                title="Select all episodes in range"
                            >
                                {lang.select_range_label}
                            </button>
                            <button
                                type="button"
                                className="k-button k-button-sm k-button-solid k-button-solid-base"
                                onClick={handleDeselectRange}
                                style={{ borderRadius: "4px", height: "30px", fontWeight: "600" }}
                                title="Deselect all episodes in range"
                            >
                                {lang.deselect_label}
                            </button>
                            <button
                                type="button"
                                className="k-button k-button-sm k-button-solid k-button-solid-base"
                                onClick={() => setEpisodeRange({ from: "", to: "" })}
                                style={{ borderRadius: "4px", height: "30px", fontWeight: "600" }}
                                title="Clear range filter"
                            >
                                {lang.clear_label}
                            </button>
                        </div>
                    </div>
                    <div>
                        {isLoading && <Loader />}
                        {!isLoading && (
                            <Grid
                                data={gridDataWithSelection}
                                style={{
                                    height: "calc(100vh - 230px)"
                                }}
                                resizable={true}
                                dataItemKey={DATA_ITEM_KEY}
                                selectedField={SELECTED_FIELD}
                                selectable={{
                                    enabled: true,
                                    drag: false,
                                    cell: false,
                                    mode: "multiple",
                                }}
                                onSelectionChange={onSelectionChange}
                                onHeaderSelectionChange={onHeaderSelectionChange}
                            >
                                <GridColumn
                                    field={SELECTED_FIELD}
                                    width="50px"
                                    headerSelectionValue={
                                        filteredGridData.length > 0 &&
                                        filteredGridData.findIndex(
                                            (item) => !selectedState[idGetter(item)]
                                        ) === -1
                                    }
                                    locked={true}
                                    filterable={false}
                                />
                                <GridColumn
                                    field="mediaEpisode.Title"
                                    title={lang.title_column}
                                />
                                <GridColumn
                                    field="mediaEpisode.WorkingTitle"
                                    title={lang.working_title_column}
                                />
                                <GridColumn
                                    field="mediaEpisode.EpisodeNumber"
                                    title={lang.episode_number_column}
                                    width="120px"
                                />
                                <GridColumn
                                    field="nodes"
                                    title={lang.nodes_label}
                                    cell={ArrayCell}
                                    format="Name"
                                    width="150px"
                                />
                                <GridColumn
                                    field="ott_platforms"
                                    title={lang.platform_label}
                                    cell={ArrayCell}
                                    format="Description"
                                    width="150px"
                                />
                                <GridColumn
                                    field="startDate"
                                    title={lang.start_date_time_label}
                                    cell={DateTimeCell}
                                    width="180px"
                                />
                                <GridColumn
                                    field="endDate"
                                    title={lang.end_date_time_label}
                                    cell={DateTimeCell}
                                    width="180px"
                                />
                            </Grid>
                        )}
                    </div>
                </div>

                {/* Right side: Form for transmission details */}
                {selectedTransmissions.length > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            height: 'calc(100vh - 185px)',
                            width: '420px',
                            background: '#fff',
                            borderLeft: '1px solid #dee2e6',
                            boxShadow: '-8px 0 20px rgba(0, 0, 0, 0.08)',
                            display: 'flex',
                            flexDirection: 'column',
                            zIndex: 3,
                            animation: "slideInRight 0.2s ease-in-out forwards"
                        }}
                    >
                        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
                            <h6 className="mb-0" style={{ fontWeight: "600", color: "#333" }}>
                                {lang.transmission_details_label}
                            </h6>
                        </div>
                        <div style={{ padding: '14px', overflowY: 'auto', flex: 1 }}>
                            <div className="row">
                                {/* Premium Date Update Section */}
                                <div className="col-12 mt-1">
                                    <div className="d-flex justify-content-between align-items-center" style={{ padding: '12px 14px', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                                        <div>
                                            <span style={{ fontWeight: "600", color: "#333", fontSize: "14px" }}>
                                                {selectedTransmissions.length === 1
                                                    ? (lang.update_selected_episode_title || "Update Selected Transmission")
                                                    : (lang.update_selected_episodes_title || "Update Selected Transmissions")}
                                            </span>
                                            <div style={{ fontSize: "11px", color: "#757575", fontWeight: "500", marginTop: "2px" }}>
                                                {selectedTransmissions.length === 1
                                                    ? (selectedTransmissions[0]?.mediaEpisode?.Title || selectedTransmissions[0]?.mediaEpisode?.WorkingTitle || `Episode ${selectedTransmissions[0]?.mediaEpisode?.EpisodeNumber}`)
                                                    : `Fill fields to apply to ${selectedTransmissions.length} selected items.`}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-success"
                                            onClick={handleBulkUpdateDates}
                                            style={{ fontWeight: "600", height: "30px", fontSize: "12px", borderRadius: "4px" }}
                                        >
                                            {lang.update_label}
                                        </button>
                                    </div>
                                    <div style={{ padding: 0, marginTop: "15px" }}>
                                        {/* Box 1: Start Date - Time and Stack */}
                                        <div className="card mb-3" style={{ borderLeft: 'none', borderRight: 'none', borderRadius: '0', backgroundColor: '#ffffff', borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '14px' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <label style={{ fontWeight: "600", fontSize: "12px", color: "#495057", textTransform: "uppercase", margin: 0 }}>
                                                    {lang.start_date_time_label}
                                                </label>
                                                <label className="mb-0 d-flex align-items-center" style={{ fontSize: "11px", fontWeight: "600", cursor: "pointer", userSelect: "none" }}>
                                                    <input
                                                        type="checkbox"
                                                        className="me-1"
                                                        checked={updateStartDate}
                                                        onChange={(e) => {
                                                            setUpdateStartDate(e.target.checked);
                                                            if (!e.target.checked) {
                                                                setUseStartStack(false);
                                                                setStartStackOffset(0);
                                                            }
                                                        }}
                                                        style={{ cursor: "pointer", width: "13px", height: "13px", margin: 0 }}
                                                    />
                                                    <span style={{ marginLeft: "5px" }}>{lang.update_label}</span>
                                                </label>
                                            </div>
                                            <div className="d-flex" style={{ gap: "10px" }}>
                                                <div style={{ flex: 1 }}>
                                                    <input
                                                        type="date"
                                                        name="bulkStartDate"
                                                        value={bulkStartDate}
                                                        onChange={(e) => setBulkStartDate(e.target.value)}
                                                        disabled={!updateStartDate}
                                                        className="form-control form-control-sm mb-2"
                                                        style={{ borderRadius: "4px" }}
                                                    />
                                                </div>
                                                <div style={{ width: "120px" }}>
                                                    <TimeRangePicker
                                                        name="bulkStartTime"
                                                        value={bulkStartTime}
                                                        onChange={(e) => setBulkStartTime(e.target.value)}
                                                        disabled={!updateStartDate}
                                                    />
                                                </div>
                                            </div>
                                            {selectedTransmissions.length > 1 && (
                                                <div className="d-flex align-items-center justify-content-between pt-2 mt-1 border-top">
                                                    <div className="d-flex align-items-center">
                                                        <input
                                                            type="checkbox"
                                                            id="start-stack-checkbox"
                                                            className="me-2"
                                                            checked={useStartStack}
                                                            onChange={(e) => setUseStartStack(e.target.checked)}
                                                            style={{ cursor: "pointer", width: "16px", height: "16px" }}
                                                            disabled={!updateStartDate}
                                                        />
                                                        <label htmlFor="start-stack-checkbox" className="mb-0" style={{ fontWeight: "600", fontSize: "12px", color: "#495057", textTransform: "uppercase", cursor: "pointer", userSelect: "none", marginLeft: "5px" }}>
                                                            {lang.start_date_stack_label}
                                                        </label>
                                                    </div>
                                                    {useStartStack && (
                                                        <div style={{ width: "130px" }}>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                style={{ borderRadius: "4px" }}
                                                                min="0"
                                                                placeholder={lang.stack_offset_label || "Offset (Days)"}
                                                                value={startStackOffset}
                                                                onChange={(e) => setStartStackOffset(parseInt(e.target.value) || 0)}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Box 2: End Date - Time and Stack */}
                                        <div className="card mb-2" style={{ borderLeft: 'none', borderRight: 'none', borderRadius: '0', backgroundColor: '#ffffff', borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '14px' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <label style={{ fontWeight: "600", fontSize: "12px", color: "#495057", textTransform: "uppercase", margin: 0 }}>
                                                    {lang.end_date_time_label}
                                                </label>
                                                <label className="mb-0 d-flex align-items-center" style={{ fontSize: "11px", fontWeight: "600", cursor: "pointer", userSelect: "none" }}>
                                                    <input
                                                        type="checkbox"
                                                        className="me-1"
                                                        checked={updateEndDate}
                                                        onChange={(e) => {
                                                            setUpdateEndDate(e.target.checked);
                                                            if (!e.target.checked) {
                                                                setUseEndStack(false);
                                                                setEndStackOffset(0);
                                                            }
                                                        }}
                                                        style={{ cursor: "pointer", width: "13px", height: "13px", margin: 0 }}
                                                    />
                                                    <span style={{ marginLeft: "5px" }}>{lang.update_label}</span>
                                                </label>
                                            </div>
                                            <div className="d-flex" style={{ gap: "10px" }}>
                                                <div style={{ flex: 1 }}>
                                                    <input
                                                        type="date"
                                                        name="bulkEndDate"
                                                        value={bulkEndDate}
                                                        onChange={(e) => setBulkEndDate(e.target.value)}
                                                        disabled={!updateEndDate}
                                                        className="form-control form-control-sm mb-2"
                                                        style={{ borderRadius: "4px" }}
                                                    />
                                                </div>
                                                <div style={{ width: "120px" }}>
                                                    <TimeRangePicker
                                                        name="bulkEndTime"
                                                        value={bulkEndTime}
                                                        onChange={(e) => setBulkEndTime(e.target.value)}
                                                        disabled={!updateEndDate}
                                                    />
                                                </div>
                                            </div>
                                            {selectedTransmissions.length > 1 && (
                                                <div className="d-flex align-items-center justify-content-between pt-2 mt-1 border-top">
                                                    <div className="d-flex align-items-center">
                                                        <input
                                                            type="checkbox"
                                                            id="end-stack-checkbox"
                                                            className="me-2"
                                                            checked={useEndStack}
                                                            onChange={(e) => setUseEndStack(e.target.checked)}
                                                            style={{ cursor: "pointer", width: "16px", height: "16px" }}
                                                            disabled={!updateEndDate}
                                                        />
                                                        <label htmlFor="end-stack-checkbox" className="mb-0" style={{ fontWeight: "600", fontSize: "12px", color: "#495057", textTransform: "uppercase", cursor: "pointer", userSelect: "none", marginLeft: "5px" }}>
                                                            {lang.end_date_stack_label}
                                                        </label>
                                                    </div>
                                                    {useEndStack && (
                                                        <div style={{ width: "130px" }}>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                style={{ borderRadius: "4px" }}
                                                                min="0"
                                                                placeholder={lang.stack_offset_label || "Offset (Days)"}
                                                                value={endStackOffset}
                                                                onChange={(e) => setEndStackOffset(parseInt(e.target.value) || 0)}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </BossDialog>
    );
};

export default EditSeriesTransmissionForm;
